/**
 * StructureLayer - Vector layer for structure polygons
 *
 * Renders invisible polygon features for hit detection.
 * The tile images already show the structures visually,
 * this layer only enables click/hover interactions.
 */

import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import { Polygon } from 'ol/geom';
import { Style, Fill, Stroke } from 'ol/style';

/**
 * StructureLayer class - manages structure polygon features
 */
export class StructureLayer {
  /**
   * Create a new StructureLayer
   * @param {import('./CellMap.js').CellMap} cellMap - The CellMap instance
   */
  constructor(cellMap) {
    this.cellMap = cellMap;
    this.source = new VectorSource();
    this.structures = new Map(); // id -> structure data
    this.loaded = false;

    // Invisible style - structures don't visually alter the map
    // They're only used for hit detection
    const invisibleStyle = new Style({
      fill: new Fill({
        color: 'rgba(0, 0, 0, 0)' // Fully transparent
      }),
      stroke: new Stroke({
        color: 'rgba(0, 0, 0, 0)', // Fully transparent
        width: 0
      })
    });

    // Debug style - uncomment to visualize structure outlines
    // Useful for development and debugging
    // const debugStyle = new Style({
    //   fill: new Fill({ color: 'rgba(255, 0, 0, 0.1)' }),
    //   stroke: new Stroke({ color: 'rgba(255, 0, 0, 0.5)', width: 2 })
    // });

    this.layer = new VectorLayer({
      source: this.source,
      style: invisibleStyle,
      className: 'structure-layer',
      // Features should be available at all zoom levels
      minResolution: 0,
      maxResolution: Infinity
    });

    // Add layer to map
    cellMap.getMap().addLayer(this.layer);
  }

  /**
   * Load structures from JSON and create features
   * @returns {Promise<void>}
   */
  async loadStructures() {
    try {
      const response = await fetch('/data/structures.json');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to load structures.json`);
      }

      const data = await response.json();

      if (!data.structures || !Array.isArray(data.structures)) {
        throw new Error('Invalid structures.json format: missing structures array');
      }

      this.createFeatures(data.structures);
      this.loaded = true;

      console.info(`StructureLayer: Loaded ${data.structures.length} structures`);
    } catch (error) {
      console.error('StructureLayer: Failed to load structures:', error);
      throw error;
    }
  }

  /**
   * Create OpenLayers features from structure data
   * @param {Array} structures - Array of structure objects
   */
  createFeatures(structures) {
    const features = [];

    for (const structure of structures) {
      // Skip structures without polygon data
      if (!structure.polygon || !Array.isArray(structure.polygon)) {
        continue;
      }

      try {
        // Ensure polygon is a closed ring
        const coords = [...structure.polygon];
        const first = coords[0];
        const last = coords[coords.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
          coords.push([...first]); // Close the ring
        }

        // Create polygon geometry
        const polygon = new Polygon([coords]);

        // Create feature
        const feature = new Feature({
          geometry: polygon
        });

        // Set feature ID (for lookup)
        feature.setId(structure.id);

        // Store all structure data as feature properties
        feature.set('structureId', structure.id);
        feature.set('name', structure.name);
        feature.set('shortDescription', structure.shortDescription);
        feature.set('position', structure.position);
        feature.set('resolution', structure.resolution);
        feature.set('wikipediaName', structure.wikipediaName);
        feature.set('category', structure.category);
        feature.set('parentId', structure.parentId);
        feature.set('children', structure.children);

        features.push(feature);

        // Store in lookup map
        this.structures.set(structure.id, structure);
      } catch (error) {
        console.warn(`StructureLayer: Failed to create feature for structure ${structure.id}:`, error);
      }
    }

    // Add all features at once (more efficient)
    this.source.addFeatures(features);
  }

  /**
   * Check if structures have been loaded
   * @returns {boolean}
   */
  isLoaded() {
    return this.loaded;
  }

  /**
   * Get the number of loaded structures
   * @returns {number}
   */
  getStructureCount() {
    return this.structures.size;
  }

  /**
   * Get structure data by ID
   * @param {number} id - Structure ID
   * @returns {Object|undefined}
   */
  getStructureById(id) {
    return this.structures.get(id);
  }

  /**
   * Get all structures
   * @returns {Array}
   */
  getAllStructures() {
    return Array.from(this.structures.values());
  }

  /**
   * Get feature by structure ID
   * @param {number} id - Structure ID
   * @returns {Feature|null}
   */
  getFeatureById(id) {
    return this.source.getFeatureById(id);
  }

  /**
   * Get features at a specific pixel position
   * @param {number[]} pixel - [x, y] pixel position
   * @returns {Feature[]} Array of features at that pixel (in z-order)
   */
  getFeaturesAtPixel(pixel) {
    const features = [];
    this.cellMap.getMap().forEachFeatureAtPixel(
      pixel,
      feature => {
        if (feature.get('structureId')) {
          features.push(feature);
        }
      },
      {
        layerFilter: layer => layer === this.layer
      }
    );
    return features;
  }

  /**
   * Get the topmost (smallest/most detailed) structure at a coordinate
   * @param {number[]} coordinate - [x, y] map coordinate
   * @returns {Object|null} Structure data or null
   */
  getStructureAtCoordinate(coordinate) {
    const pixel = this.cellMap.getMap().getPixelFromCoordinate(coordinate);
    const features = this.getFeaturesAtPixel(pixel);

    if (features.length === 0) {
      return null;
    }

    // Return the structure with the highest resolution (most detailed/smallest)
    // Higher resolution value = smaller feature = should be selected first
    let best = features[0];
    let bestResolution = best.get('resolution') || 0;

    for (const feature of features) {
      const resolution = feature.get('resolution') || 0;
      if (resolution > bestResolution) {
        best = feature;
        bestResolution = resolution;
      }
    }

    return this.getStructureById(best.get('structureId'));
  }

  /**
   * Build ancestor tree for a structure
   * @param {number} structureId - Starting structure ID
   * @returns {Array} Array of ancestor structures from root to current
   */
  getAncestorTree(structureId) {
    const ancestors = [];
    let current = this.structures.get(structureId);

    while (current) {
      ancestors.unshift(current);
      if (current.parentId !== null && current.parentId !== undefined) {
        current = this.structures.get(current.parentId);
      } else {
        break;
      }
    }

    return ancestors;
  }

  /**
   * Build ancestor tree as formatted string
   * @param {number} structureId - Starting structure ID
   * @param {string} separator - Separator between names
   * @returns {string} Formatted ancestor path
   */
  getAncestorPath(structureId, separator = ' → ') {
    const ancestors = this.getAncestorTree(structureId);
    return ancestors.map(s => s.name).join(separator);
  }

  /**
   * Get direct children of a structure
   * @param {number} structureId - Parent structure ID
   * @returns {Array} Array of child structure objects
   */
  getChildren(structureId) {
    const structure = this.structures.get(structureId);
    if (!structure || !structure.children || !Array.isArray(structure.children)) {
      return [];
    }

    return structure.children
      .map(childId => this.structures.get(childId))
      .filter(Boolean);
  }

  /**
   * Get all descendants of a structure (recursive)
   * @param {number} structureId - Parent structure ID
   * @returns {Array} Array of all descendant structure objects
   */
  getDescendants(structureId) {
    const descendants = [];
    const queue = [structureId];

    while (queue.length > 0) {
      const id = queue.shift();
      const children = this.getChildren(id);

      for (const child of children) {
        descendants.push(child);
        queue.push(child.id);
      }
    }

    return descendants;
  }

  /**
   * Search structures by name (case-insensitive partial match)
   * @param {string} query - Search query
   * @returns {Array} Matching structures
   */
  searchByName(query) {
    if (!query || query.length === 0) {
      return [];
    }

    const lowerQuery = query.toLowerCase();
    return this.getAllStructures().filter(
      s => s.name && s.name.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Get structures by category
   * @param {string} category - Category name
   * @returns {Array} Structures in that category
   */
  getByCategory(category) {
    return this.getAllStructures().filter(s => s.category === category);
  }

  /**
   * Get the OpenLayers layer
   * @returns {VectorLayer}
   */
  getLayer() {
    return this.layer;
  }

  /**
   * Get the OpenLayers source
   * @returns {VectorSource}
   */
  getSource() {
    return this.source;
  }
}
